import { Switch } from '@/components/ui/switch';
import { useTracking } from '@/contexts/TrackingContext';
import { useFocusTrap } from '@/hooks/use-focus-trap';
import { AnimatePresence, motion } from 'framer-motion';
import { Shield, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const EASE = [0.32, 0.72, 0, 1] as const;

export default function CookieConsent() {
    const { config, consent, updateConsent } = useTracking();
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [categories, setCategories] = useState({
        necessary: true,
        analytics: true,
        marketing: true,
        functional: true,
    });

    const settingsRef = useRef<HTMLDivElement>(null);
    useFocusTrap(settingsRef, showSettings);

    // ── Handlers (defined before effects that reference them) ──

    const handleAcceptAll = useCallback(() => {
        const allCategories = {
            necessary: true,
            analytics: true,
            marketing: true,
            functional: true,
        };
        updateConsent(allCategories);
        setShowBanner(false);
        setShowSettings(false);
    }, [updateConsent]);

    const handleAcceptSelected = useCallback(() => {
        updateConsent(categories);
        setShowBanner(false);
        setShowSettings(false);
    }, [updateConsent, categories]);

    const handleRejectAll = useCallback(() => {
        const minimalCategories = {
            necessary: true,
            analytics: false,
            marketing: false,
            functional: false,
        };
        updateConsent(minimalCategories);
        setShowBanner(false);
        setShowSettings(false);
    }, [updateConsent]);

    const handleCloseSettings = useCallback(() => {
        setShowSettings(false);
        if (!consent?.given) {
            setShowBanner(true);
        }
    }, [consent?.given]);

    const toggleCategory = useCallback((category: keyof typeof categories) => {
        if (category === 'necessary') return;
        setCategories((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    }, []);

    // ── Effects ──

    useEffect(() => {
        if (config?.cookieConsent?.enabled && !consent?.given) {
            setShowBanner(true);
            setCategories({
                necessary: true,
                analytics: true,
                marketing: true,
                functional: true,
            });
        }
    }, [config, consent]);

    useEffect(() => {
        if (consent?.given && consent?.categories) {
            setCategories(consent.categories);
        }
    }, [consent]);

    // Body scroll lock when settings modal is open
    useEffect(() => {
        if (showSettings) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showSettings]);

    // Escape key closes settings modal
    useEffect(() => {
        if (!showSettings) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCloseSettings();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showSettings, handleCloseSettings]);

    // Bug fix: only bail if cookie consent is disabled — not if banner is hidden
    // This allows the floating reopen button to render after consent is given
    if (!config?.cookieConsent?.enabled) {
        return null;
    }

    const consentGiven = !!consent?.given;
    const showFloatingButton = !showBanner && !showSettings && consentGiven;

    return (
        <>
            {/* ─── Slim Bottom Bar ─── */}
            <AnimatePresence>
                {showBanner && !showSettings && (
                    <motion.div
                        key="consent-bar"
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ duration: 0.5, ease: EASE }}
                        className="fixed right-0 bottom-0 left-0 z-50"
                        role="dialog"
                        aria-label="Cookie consent"
                    >
                        <div className="border-t border-[#F5F5F5]/10 bg-[#0e2727] px-5 py-3 sm:px-8 sm:py-3.5">
                            <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
                                {/* Left: message + manage link */}
                                <p className="lux-body lux-body-sm text-[#F5F5F5]/80">
                                    We use cookies to personalise your experience.{' '}
                                    <button
                                        onClick={() => {
                                            setShowBanner(false);
                                            setShowSettings(true);
                                        }}
                                        className="text-[#F5F5F5] underline underline-offset-2 transition-opacity hover:opacity-70"
                                    >
                                        Manage preferences
                                    </button>
                                </p>

                                {/* Right: actions */}
                                <div className="flex shrink-0 items-center gap-2.5">
                                    <button
                                        onClick={handleRejectAll}
                                        className="lux-caps lux-caps-sm hidden rounded-sm border border-[#F5F5F5]/20 px-4 py-1.5 text-[#F5F5F5]/70 transition-colors hover:border-[#F5F5F5]/40 hover:text-[#F5F5F5] sm:inline-flex"
                                    >
                                        Decline
                                    </button>
                                    <button
                                        onClick={handleAcceptAll}
                                        className="lux-caps lux-caps-sm rounded-sm border border-[#F5F5F5]/30 bg-[#F5F5F5]/10 px-4 py-1.5 text-[#F5F5F5] transition-colors hover:border-[#F5F5F5]/50 hover:bg-[#F5F5F5]/15"
                                    >
                                        Accept All
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ─── Settings Modal ─── */}
            <AnimatePresence>
                {showSettings && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            key="consent-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3, ease: EASE }}
                            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
                            onClick={handleCloseSettings}
                            aria-hidden="true"
                        />

                        {/* Panel */}
                        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
                            <motion.div
                                key="consent-panel"
                                ref={settingsRef}
                                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96, y: 20 }}
                                transition={{ duration: 0.4, ease: EASE }}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Privacy preferences"
                                className="relative w-full max-w-lg rounded-sm bg-[#0e2727] shadow-2xl"
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between border-b border-[#F5F5F5]/10 px-6 py-5">
                                    <h2 className="lux-didot lux-didot-md text-[#F5F5F5]">Privacy Preferences</h2>
                                    <button
                                        onClick={handleCloseSettings}
                                        className="rounded-sm p-1 text-[#F5F5F5]/50 transition-colors hover:text-[#F5F5F5]"
                                        aria-label="Close privacy preferences"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Categories */}
                                <div className="max-h-[60vh] space-y-0 overflow-y-auto">
                                    {Object.entries(config.cookieConsent.categories || {}).map(([key, category], idx, arr) => (
                                        <div
                                            key={key}
                                            className={`flex items-start justify-between gap-4 px-6 py-4 ${idx < arr.length - 1 ? 'border-b border-[#F5F5F5]/5' : ''}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-[#F5F5F5]">{category.title}</span>
                                                    {category.readonly && <span className="lux-caps lux-caps-sm text-[#F5F5F5]/40">Required</span>}
                                                </div>
                                                <p className="lux-body lux-body-sm mt-1 text-[#F5F5F5]/50">{category.description}</p>
                                                {category.services && category.services.length > 0 && (
                                                    <p className="lux-body lux-body-sm mt-1.5 text-[#F5F5F5]/35">{category.services.join(' · ')}</p>
                                                )}
                                            </div>
                                            <Switch
                                                checked={categories[key as keyof typeof categories]}
                                                disabled={category.readonly}
                                                onCheckedChange={() => toggleCategory(key as keyof typeof categories)}
                                                aria-label={`${category.title} cookies`}
                                                className="mt-0.5 data-[state=checked]:bg-[#BF1322]"
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Footer */}
                                <div className="flex items-center gap-2.5 border-t border-[#F5F5F5]/10 px-6 py-4">
                                    <button
                                        onClick={handleRejectAll}
                                        className="lux-caps lux-caps-sm flex-1 rounded-sm border border-[#F5F5F5]/20 py-2 text-[#F5F5F5]/70 transition-colors hover:border-[#F5F5F5]/40 hover:text-[#F5F5F5]"
                                    >
                                        Decline All
                                    </button>
                                    <button
                                        onClick={handleAcceptSelected}
                                        className="lux-caps lux-caps-sm flex-1 rounded-sm border border-[#F5F5F5]/30 bg-[#F5F5F5]/10 py-2 text-[#F5F5F5] transition-colors hover:border-[#F5F5F5]/50 hover:bg-[#F5F5F5]/15"
                                    >
                                        Save Preferences
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </>
                )}
            </AnimatePresence>

            {/* ─── Floating Reopen Button ─── */}
            <AnimatePresence>
                {showFloatingButton && (
                    <motion.button
                        key="consent-reopen"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                        transition={{ duration: 0.4, ease: EASE, delay: 0.5 }}
                        onClick={() => setShowSettings(true)}
                        className="fixed bottom-4 left-4 z-40 flex items-center gap-1.5 rounded-full bg-[#0e2727] px-3 py-1.5 shadow-lg transition-shadow hover:shadow-xl"
                        aria-label="Open privacy settings"
                    >
                        <Shield className="h-3.5 w-3.5 text-[#F5F5F5]/70" />
                        <span className="lux-caps lux-caps-sm text-[#F5F5F5]/70">Privacy</span>
                    </motion.button>
                )}
            </AnimatePresence>
        </>
    );
}
